import { Component, ChangeDetectionStrategy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from '@progress/kendo-angular-dialog';

import { PolicyDocument, ControlDomain } from '../../services/policy-gap-analysis.service';

@Component({
  selector: 'app-document-upload-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  templateUrl: './document-upload-dialog.component.html',
  styleUrl: './document-upload-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentUploadDialogComponent {
  uploaded = output<PolicyDocument>();
  cancelled = output<void>();

  public selectedFileName = signal<string | null>(null);
  public selectedFileType = signal<'pdf' | 'docx' | null>(null);
  public isUploading = signal<boolean>(false);
  public isDragOver = signal<boolean>(false);

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  public onDragLeave(): void {
    this.isDragOver.set(false);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    // Mock: simulate file selection
    this.selectMockFile('Dropped Security Policy.pdf', 'pdf');
  }

  public onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const ext = file.name.toLowerCase().endsWith('.docx') ? 'docx' : 'pdf';
      this.selectMockFile(file.name, ext);
    }
  }

  public selectMockFile(name: string, type: 'pdf' | 'docx'): void {
    this.selectedFileName.set(name);
    this.selectedFileType.set(type);
  }

  public onUpload(): void {
    if (!this.selectedFileName()) return;

    this.isUploading.set(true);

    // Simulate upload delay
    setTimeout(() => {
      const mockDoc: PolicyDocument = {
        id: Date.now(),
        file_name: this.selectedFileName()!,
        file_type: this.selectedFileType()!,
        uploaded_at: new Date().toISOString(),
        uploaded_by: 'current.user@company.com',
        status: 'processing',
        domains: [],
        statement_count: 0,
        report_card: null,
      };

      this.isUploading.set(false);
      this.uploaded.emit(mockDoc);

      // Simulate analysis completion after 3 seconds
      setTimeout(() => {
        const analyzedDoc: PolicyDocument = {
          ...mockDoc,
          status: 'analyzed',
          domains: ['access_control', 'endpoint_security'] as ControlDomain[],
          statement_count: 8,
          report_card: {
            alignment_label: 'moderate',
            validated_count: 3,
            documented_not_validated_count: 2,
            recommended_not_documented_count: 1,
            not_verifiable_count: 1,
            potentially_verifiable_count: 1,
            total_findings: 8,
          },
        };
        // Note: In a real app this would come through a WebSocket or polling mechanism
        console.log('Analysis complete for:', analyzedDoc.file_name);
      }, 3000);
    }, 1500);
  }

  public onCancel(): void {
    this.cancelled.emit();
  }
}
